import { useCallback, useEffect, useState } from 'react';
import { SelectForm } from '../../../components/select-form/select-form';
import { Spinner } from '../../../components/spinner/spinner';
import { useAPI } from '../../../clients/quipli-service/quipli-service-client';
import {
  ConfigBinaryProperty,
  ConfigInputProperty,
  ConfigProperty,
  ConfigSection,
} from '../components/config-property';
import { debounce } from 'lodash';
import usePrevious from '../../../hooks/use-previous';
import { toast } from '../../../util/toasts';
import { TaxConfigType, TaxJurisdiction } from '../../../clients/quipli-service/tax/tax';
import { useTaxApiV1 } from '../../../clients/quipli-service/tax/tax-api-v1';

const TAX_OPTIONS = [
  {
    value: TaxConfigType.CustomerZipCode,
    label: 'Customer Zip Code',
  },
  {
    value: TaxConfigType.LocationZipCode,
    label: 'Store Location Zip Code',
  },
  {
    value: TaxConfigType.Global,
    label: 'Global Flat Tax Rate',
  },
  {
    value: TaxConfigType.City,
    label: 'Tax By City',
  },
  {
    value: TaxConfigType.Avalara,
    label: 'Avalara Tax Library',
  },
  {
    value: TaxConfigType.TaxRate,
    label: 'Tax Code Setup',
  },
];

const TAX_PRICE_LABELS = [
  { value: 'Tax', label: 'Tax' },
  { value: 'GST', label: 'GST' },
  { value: 'VAT', label: 'VAT' },
  { value: 'VST', label: 'VST' },
  { value: 'custom', label: 'Custom Text' },
];

const TAX_OPTION_DESCRIPTIONS = {
  [TaxConfigType.CustomerZipCode]: `For delivery orders, this tax option will apply a tax percentage 
    based on the <b>zip code of the customer’s delivery address</b>. For pick up orders, a tax 
    percentage will be applied based on the <b>zip code of the store location address</b>. 
    Please make sure to check if your store location city is configured correctly under 
    <b>Inventory > Locations</b>.`,
  [TaxConfigType.LocationZipCode]: `This tax option will apply a tax based on the zip code of store 
    location. Please make sure to check if your store location zip code is configured correctly 
    under <b>Inventory > Locations</b>`,
  [TaxConfigType.Global]: `This is a flat percentage (%) that will be applicable to all orders.`,
  [TaxConfigType.City]: `For delivery orders, this tax option will apply a tax percentage based on 
    the <b>city of the customer’s delivery address</b>. For pick up orders, a tax percentage will 
    be applied based on the <b>city of the store location address</b>. Please make sure to check 
    if your store location city is configured correctly under <b>Inventory > Locations</b>.<br>
    <b>If no tax percentage is found based on city, system will default to tax percentage entered 
    below.</b>`,
  [TaxConfigType.Avalara]: `Avalara is a 3rd party service which calculates tax based on the 
    customers delivery address`,
  [TaxConfigType.TaxRate]: '',
};

export const TaxConfig = () => {
  const { getMultipleConfigurations, updateConfiguration } = useAPI();
  const { searchTaxCodes, createTaxCode, createTaxJurisdiction, updateTaxJurisdiction } = useTaxApiV1();

  const [loading, setLoading] = useState<boolean>(false);
  const [taxConfig, setTaxConfig] = useState<TaxConfigType>();
  const prevTaxConfig = usePrevious(taxConfig);
  const [taxConfigDescription, setTaxConfigDescription] = useState<string>('');
  const [enableFlatTaxRate, setEnableFlatTaxRate] = useState<boolean>(false);
  const [flatTaxRate, setFlatTaxRate] = useState<number>();
  const prevFlatTaxRate = usePrevious(flatTaxRate);
  const [useTaxJar, setUseTaxJar] = useState<boolean>(false);
  const prevUseTaxJar = usePrevious(useTaxJar);
  const [displayTaxJurisdictionBreakdown, setDisplayTaxJurisdictionBreakdown] = useState<boolean>(false);
  const prevDisplayTaxJurisdictionBreakdown = usePrevious(displayTaxJurisdictionBreakdown);
  const [defaultUserTaxCode, setDefaultUserTaxCode] = useState<boolean>(false);
  const [taxInclusivePricing, setTaxInclusivePricing] = useState<boolean>(false);
  const prevTaxInclusivePricing = usePrevious(taxInclusivePricing);
  const [taxLabel, setTaxLabel] = useState<string>('Tax');
  const prevTaxLabel = usePrevious(taxLabel);
  const prevDefaultUserTaxCode = usePrevious(defaultUserTaxCode);
  const [globalTaxJurisdiction, setGlobalTaxJurisdiction] = useState<TaxJurisdiction>();

  const debouncedUpdateTaxConfig = useCallback(
    debounce((taxConfig: TaxConfigType) => {
      updateConfiguration('CONF_SITE_TAX_OPTIONS', taxConfig.toString()).catch(() => {
        toast('Failed to update tax option', 'error');
      });
    }, 500),
    [],
  );

  const debouncedUpdateFlatTaxRate = useCallback(
    debounce((taxRate: number) => {
      updateConfiguration('CONF_SITE_TAX', taxRate.toString()).catch(() => {
        toast('Failed to update default tax', 'error');
      });
    }, 500),
    [],
  );

  const debouncedUpdateUseTaxJar = useCallback(
    debounce((userTaxJar: boolean) => {
      updateConfiguration('CONF_TAXJAR', userTaxJar ? '1' : '0').catch(() => {
        toast('Failed to update use TaxJar setting', 'error');
      });
    }, 500),
    [],
  );

  const debouncedUpdateTaxInclusivePricing = useCallback(
    debounce((userTaxInclusivePricing: boolean) => {
      updateConfiguration('CONF_SHOW_PRICES_WITH_TAX', userTaxInclusivePricing ? '1' : '0').catch(() => {
        toast('Failed to update tax inclusive pricing setting', 'error');
      });
    }, 500),
    [],
  );

  const debouncedUpdateTaxLabel = useCallback(
    debounce((userTaxLabel: string) => {
      updateConfiguration('CONF_PRICE_TAX_LABEL', userTaxLabel).catch(() => {
        toast('Failed to update tax label setting', 'error');
      });
    }, 500),
    [],
  );

  const debouncedUpdateDisplayTaxJurisdictionBreakdown = useCallback(
    debounce((displayTaxJurisdictionBreakdown: boolean) => {
      updateConfiguration(
        'CONF_DISPLAY_JURISDICTION_TAXES_BREAKDOWN',
        displayTaxJurisdictionBreakdown ? '1' : '0',
      ).catch(() => {
        toast('Failed to update display individual jurisdictions setting', 'error');
      });
    }, 500),
    [],
  );

  const debouncedUpdateDefaultUserTaxCode = useCallback(
    debounce((defaultUserTaxCode: boolean) => {
      updateConfiguration('CONF_DEFAULT_USER_TAX_CODE', defaultUserTaxCode ? '1' : '0').catch(() => {
        toast('Failed to update default customer tax code setting', 'error');
      });
    }, 500),
    [],
  );

  const debouncedCreateTaxJurisdictionAndTaxCode = useCallback(
    debounce(async (name: string, percentage: number) => {
      if (!name || !percentage) return;

      try {
        const taxJurisdiction = await createTaxJurisdiction({ name, percentage });
        setGlobalTaxJurisdiction(taxJurisdiction);

        await createTaxCode({ name, jurisdictionIds: [taxJurisdiction.id], source: 'manual' });
      } catch (error) {
        toast('Failed to create default tax code', 'error');
      }
    }, 1000),
    [],
  );

  const debouncedUpdateTaxJurisdiction = useCallback(
    debounce(async (taxJurisdiction: TaxJurisdiction) => {
      if (!taxJurisdiction) return;

      try {
        const updatedTaxJurisdiction = await updateTaxJurisdiction(taxJurisdiction);
        setGlobalTaxJurisdiction(updatedTaxJurisdiction);
      } catch (error) {
        toast('Failed to update default tax jurisdiction', 'error');
      }
    }, 500),
    [],
  );

  useEffect(() => {
    const fetchTaxConfiguration = async () => {
      setLoading(true);

      const configs = await getMultipleConfigurations([
        'CONF_SITE_TAX_OPTIONS',
        'CONF_SITE_TAX',
        'CONF_TAXJAR',
        'CONF_DISPLAY_JURISDICTION_TAXES_BREAKDOWN',
        'CONF_DEFAULT_USER_TAX_CODE',
        'CONF_SHOW_PRICES_WITH_TAX',
        'CONF_PRICE_TAX_LABEL',
      ]);

      const taxSettingConfig = parseInt(configs.find((c) => c.variable === 'CONF_SITE_TAX_OPTIONS')?.value ?? '0');
      setTaxConfig(taxSettingConfig as TaxConfigType);
      setTaxConfigDescription(TAX_OPTION_DESCRIPTIONS[taxSettingConfig as TaxConfigType]);
      setEnableFlatTaxRate((taxSettingConfig as TaxConfigType) === TaxConfigType.Global);
      setFlatTaxRate(parseFloat(configs.find((c) => c.variable === 'CONF_SITE_TAX')?.value ?? '0'));
      setUseTaxJar(configs.find((c) => c.variable === 'CONF_TAXJAR')?.value === '1');
      setTaxInclusivePricing(configs.find((c) => c.variable === 'CONF_SHOW_PRICES_WITH_TAX')?.value === '1');
      setTaxLabel(configs.find((c) => c.variable === 'CONF_PRICE_TAX_LABEL')?.value ?? 'Tax');
      setDisplayTaxJurisdictionBreakdown(
        configs.find((c) => c.variable === 'CONF_DISPLAY_JURISDICTION_TAXES_BREAKDOWN')?.value === '1',
      );
      setDefaultUserTaxCode(configs.find((c) => c.variable === 'CONF_DEFAULT_USER_TAX_CODE')?.value === '1');

      const taxCodes = await searchTaxCodes({ keyword: 'Global' });
      if (taxCodes.items[0] && taxCodes.items[0].jurisdictions[0]) {
        setGlobalTaxJurisdiction(taxCodes.items[0].jurisdictions[0]);
        setFlatTaxRate(taxCodes.items[0].jurisdictions[0].percentage);
      }

      setLoading(false);
    };

    fetchTaxConfiguration().catch((e: any) => {
      toast(`Failed to load tax configuration ${e}`, 'error');
      console.log(e);
    });
  }, []);

  useEffect(() => {
    if (!loading && prevTaxConfig !== undefined && taxConfig !== undefined && prevTaxConfig !== taxConfig) {
      debouncedUpdateTaxConfig(taxConfig);
      setTaxConfigDescription(TAX_OPTION_DESCRIPTIONS[taxConfig]);
      setEnableFlatTaxRate(taxConfig === TaxConfigType.Global);
    }
  }, [taxConfig]);

  useEffect(() => {
    if (!loading && prevFlatTaxRate && flatTaxRate && prevFlatTaxRate !== flatTaxRate) {
      debouncedUpdateFlatTaxRate(flatTaxRate);

      if (globalTaxJurisdiction) {
        const updatedTaxJurisdiction = {
          ...globalTaxJurisdiction,
          percentage: flatTaxRate,
        };

        debouncedUpdateTaxJurisdiction(updatedTaxJurisdiction);
      } else {
        debouncedCreateTaxJurisdictionAndTaxCode('Global', flatTaxRate);
      }
    }
  }, [flatTaxRate]);

  useEffect(() => {
    if (!loading && prevUseTaxJar !== undefined && prevUseTaxJar !== useTaxJar) {
      debouncedUpdateUseTaxJar(useTaxJar);
    }
  }, [useTaxJar]);

  useEffect(() => {
    if (!loading && prevTaxInclusivePricing !== undefined && prevTaxInclusivePricing !== taxInclusivePricing) {
      debouncedUpdateTaxInclusivePricing(taxInclusivePricing);
    }
  }, [taxInclusivePricing]);

  useEffect(() => {
    if (!loading && prevTaxLabel !== undefined && prevTaxLabel !== taxLabel) {
      debouncedUpdateTaxLabel(taxLabel);
    }
  }, [taxLabel]);

  useEffect(() => {
    if (
      !loading &&
      prevDisplayTaxJurisdictionBreakdown !== undefined &&
      prevDisplayTaxJurisdictionBreakdown !== displayTaxJurisdictionBreakdown
    ) {
      debouncedUpdateDisplayTaxJurisdictionBreakdown(displayTaxJurisdictionBreakdown);
    }
  }, [displayTaxJurisdictionBreakdown]);
  useEffect(() => {
    if (!loading && prevDefaultUserTaxCode !== undefined && prevDefaultUserTaxCode !== defaultUserTaxCode) {
      debouncedUpdateDefaultUserTaxCode(defaultUserTaxCode);
    }
  }, [defaultUserTaxCode]);

  return taxConfig != undefined ? (
    <ConfigSection>
      <ConfigProperty property="Tax Options" description={taxConfigDescription}>
        <SelectForm
          value={TAX_OPTIONS.find((op) => op.value === taxConfig)}
          options={TAX_OPTIONS}
          onChange={(op) => op && setTaxConfig(op.value)}
        />
      </ConfigProperty>
      <ConfigInputProperty
        property="Default Tax"
        description="% Global Tax applicable on products."
        type="number"
        value={flatTaxRate}
        onChange={(value: number) => setFlatTaxRate(value >= 0 ? value : 0)}
        disabled={!enableFlatTaxRate}
      />
      <ConfigBinaryProperty
        property="Use TaxJar with Tax code setup"
        value={useTaxJar}
        onChange={(value: boolean) => setUseTaxJar(value)}
      />
      <ConfigBinaryProperty
        property="Display individual jurisdiction taxes on invoices"
        value={displayTaxJurisdictionBreakdown}
        onChange={(value: boolean) => setDisplayTaxJurisdictionBreakdown(value)}
      />
      <ConfigBinaryProperty
        property="Enable Default Customer Tax Codes"
        value={defaultUserTaxCode}
        onChange={(value: boolean) => setDefaultUserTaxCode(value)}
      />
      <ConfigBinaryProperty
        property="Display Prices with Tax Included"
        description={
          'Enable this setting to display all prices with tax included on customer-facing pages, invoices, and order summaries. Product pricing will still be entered as tax-exclusive in the admin console.'
        }
        value={taxInclusivePricing}
        onChange={(value: boolean) => setTaxInclusivePricing(value)}
      />
      {taxInclusivePricing ? (
        <ConfigProperty property="Tax label for Displayed Prices">
          <SelectForm
            value={TAX_PRICE_LABELS.find((op) => op.value === taxLabel)}
            options={TAX_PRICE_LABELS}
            onChange={(op) => op && setTaxLabel(op.value)}
          />
        </ConfigProperty>
      ) : (
        <></>
      )}
      {!TAX_PRICE_LABELS.map((_) => _.value)
        .filter((_) => _ !== 'custom')
        .includes(taxLabel) && taxInclusivePricing ? (
        <ConfigInputProperty
          property="Custom Text"
          description="Label for tax on customer facing pages, invoices, and order summaries."
          type="text"
          value={taxLabel}
          onChange={(value: string) => setTaxLabel(value)}
        />
      ) : (
        <></>
      )}
    </ConfigSection>
  ) : (
    <Spinner />
  );
};
